import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { bool } from 'prop-types';
import { Favorite, Profile } from '@nike/nike-design-system-icons';
import { IconButton, useMatchMedia, useBreakpoint } from '@nike/nike-design-system-components';
import { Global } from '@emotion/react';
import clsx from 'clsx';
import Link from '../Link';
import VisualSearch from '../VisualSearch';
import MobileNavBtn from '../MobileNavBtn';
import MobileMenu from '../MobileMenu';
import {
  StyledDiv,
  globalStyleNavOverflow,
  MOBILE_BTN_CLASS,
  profileImageClass,
  profileLinkClass,
  navFavorites,
  isVisible as navFavoritesVisibleClass,
} from './styles.js';
import { BagWithCount } from './bagWithCount.js';
import {
  DATA_TYPES,
  MENU_TYPES,
  GLOBAL_NAV_SEARCH,
  GLOBAL_NAV_MENU,
  BODEGA_DEFAULTS,
} from '../../constants.js';
import useIdentity from '../../utils/hooks/identityHook';
import useMenuData from '../../utils/hooks/cmsDataHook';
import { AnalyticsContext } from '../../context/analyticsContext';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import useBodega from '../../utils/hooks/bodegaHook.js';
import { navMenuOpen } from '../../utils/sharedStyles.js';
import { useCartAndFavorites } from '../../utils/hooks/useCartAndFavorites.js';
import useExperiment from '../../utils/hooks/experimentHook.js';
import { useNavOptionsContext } from '../../context/optionsContext';

/**
 * Renders the user tools component of the header, e.g., search, favorites, cart, profile icons
 * @param {object} props
 * @param {boolean} props.showExposedSearch
 * @returns {React.ReactElement}
 */
const UserTools = ({ showExposedSearch = false }) => {
  const { mounted } = useNavOptionsContext() || {};
  const isMobileQuery = useMatchMedia(useBreakpoint('lt', 'm'));
  // Prevent hydration mismatch: assume desktop during SSR and initial render
  const isMobile = mounted ? isMobileQuery : false;
  const [ariaLabelForAccount, ariaLabelForFavorites, ariaLabelForSignIn, ariaLabelForCart] = [
    ['nav_header_label_account', BODEGA_DEFAULTS.ACCOUNT],
    ['nav_header_label_favorites', BODEGA_DEFAULTS.FAVORITES],
    ['nav_title_signin_membership', BODEGA_DEFAULTS.SIGN_IN],
    ['nav-header-label-carticon', BODEGA_DEFAULTS.BAG],
  ].map(([key, def]) => useBodega(key, def));
  const showDesktopContent = !isMobile;
  const { analyticsClient } = useContext(AnalyticsContext);
  const [isMobileNavDrawerOpen, setMobileNavDrawer] = useState(false);
  const shouldFocusMobileMenuBtn = useRef(null);
  const isSearchEnabled = useFeatureFlags(GLOBAL_NAV_SEARCH);
  const isNavMenuEnabled = useFeatureFlags(GLOBAL_NAV_MENU);
  const { isCartEnabled, isFavoritesEnabled, itemsInCart, cartUrl, favsUrl } =
    useCartAndFavorites();
  const experimentResult = useExperiment([{ name: 'global_nav_favorites' }]);
  const [showFavorites, setShowFavorites] = useState(false);
  useEffect(() => {
    if (experimentResult && experimentResult?.locations?.length > 0) {
      const experiment = experimentResult.locations.find(
        item => item.name === 'global_nav_favorites',
      );
      setShowFavorites(experiment?.content?.favoritesIconVisibility);
    }
  }, [experimentResult]);

  const { isLoggedIn: userIsLoggedIn, userProfile } = useIdentity();
  const accountMenu = useMenuData(DATA_TYPES.HEADER, MENU_TYPES.ACCOUNT_SETTINGS_MENU)
    ?.children?.[0];

  const profileLink = accountMenu?.url;

  useEffect(() => {
    if (showDesktopContent && isMobileNavDrawerOpen) {
      setMobileNavDrawer(false);
    }
  }, [showDesktopContent, isMobileNavDrawerOpen]);

  const handleCartClick = event => {
    event.preventDefault();
    analyticsClient?.clickNavCart();
    window.location.assign(event.currentTarget.href);
  };

  const handleFavoritesClick = event => {
    event.preventDefault();
    analyticsClient?.clickNavShortcut({ path: 'favorites' });
    window.location.assign(event.currentTarget.href);
  };

  const handleProfileClick = () => {
    analyticsClient?.clickNavMembership({ path: 'sign in' });
    window.webShellClient.identity.signIn().finally();
  };

  useEffect(() => {
    document.body.classList[isMobileNavDrawerOpen ? 'add' : 'remove'](navMenuOpen);
    return () => document.body.classList.remove(navMenuOpen);
  }, [isMobileNavDrawerOpen]);

  return (
    <StyledDiv data-testid={'user-tools-container'} showDesktopContent={showDesktopContent}>
      <Global styles={globalStyleNavOverflow} />
      {!showExposedSearch && isSearchEnabled && <VisualSearch />}
      {!showDesktopContent && !userIsLoggedIn && (
        <IconButton
          Icon={<Profile />}
          ariaLabel={ariaLabelForSignIn}
          background="dark"
          data-testid="sign-in-button"
          onClick={handleProfileClick}
          size="small"
        />
      )}
      {!showDesktopContent && userIsLoggedIn && (
        <Link
          ariaLabel={ariaLabelForAccount}
          className={profileLinkClass}
          href={profileLink}
          onClick={() => {
            analyticsClient?.clickNavAccount({ path: 'myAccount:profile' });
          }}
        >
          {userProfile?.avatarUrl ? (
            <img className={profileImageClass} src={userProfile.avatarUrl} />
          ) : (
            <Profile />
          )}
        </Link>
      )}
      {isFavoritesEnabled && (
        <IconButton
          Component="a"
          Icon={<Favorite />}
          ariaLabel={ariaLabelForFavorites}
          className={clsx(navFavorites, showFavorites && navFavoritesVisibleClass)}
          data-testid="favorite-button"
          href={favsUrl}
          onClick={handleFavoritesClick}
          size="small"
          title={ariaLabelForFavorites}
        />
      )}
      {isCartEnabled && (
        <IconButton
          Component="a"
          Icon={<BagWithCount count={itemsInCart} />}
          ariaLabel={`${ariaLabelForCart}: ${itemsInCart}`}
          background="dark"
          className="nav-bag-icon"
          href={cartUrl}
          onClick={handleCartClick}
          size="small"
          title={`${ariaLabelForCart}: ${itemsInCart}`}
        />
      )}
      {!showDesktopContent && isNavMenuEnabled && (
        <Fragment>
          <MobileNavBtn
            className={MOBILE_BTN_CLASS}
            setMobileNavDrawer={setMobileNavDrawer}
            shouldFocusMobileMenuBtn={shouldFocusMobileMenuBtn}
          />
          <MobileMenu
            isMobileNavDrawerOpen={isMobileNavDrawerOpen}
            setMobileNavDrawer={setMobileNavDrawer}
            shouldFocusMobileMenuBtn={shouldFocusMobileMenuBtn}
          />
        </Fragment>
      )}
    </StyledDiv>
  );
};

export default UserTools;

UserTools.propTypes = {
  showExposedSearch: bool,
};
